import React, { useEffect, useState } from "react";
import { AppRouter } from "./routes/AppRouter";
import { LoginRouter } from "./routes/LoginRouter";
import { useAppDispatch, useAppSelector } from "./hooks/store.hook";
import { setParentUser, setProfile } from "./stores/actions/user.actions";
import AuthService from "./services/api/auth/Auth.service";
import {
  resetConfigState,
  setConfig,
  setOrgSettings,
} from "./stores/actions/config.actions";
import ConfigurationsService from "./services/api/configurations/Configurations.service";
import OrgService from "./services/api/org/Org.service";
import { Loader } from "./components/core/Loader/Loader";
import { resetCollaboratorState } from "./stores/actions/collaborator.actions";
import { resetFormationState } from "./stores/actions/formation.actions";
import { resetManagementState } from "./stores/actions/management.actions";
import { AxiosError } from "axios";
import { User } from "./types/api/auth.type";
import { StatusCodes } from "./constants/AppConstants";
import { ErrorRouter } from "./routes/ErrorRouter";
import { resetCareerManagersState } from "./stores/actions/careerManager.actions";
import posthog from "posthog-js";
import { theme } from "./constants/StyledComponents";
import { useDimensions } from "./hooks/useDimensions.hook";

export const App = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, profile } = useAppSelector((state) => state.user);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState<
    "forbidden" | "unauthorized" | "maintenance" | "notDesktop" | null
  >(null);
  const { width } = useDimensions();

  const resetAllStates = () => {
    dispatch(resetConfigState());
    dispatch(resetCollaboratorState());
    dispatch(resetFormationState());
    dispatch(resetManagementState());
    dispatch(resetCareerManagersState());
  };

  const loadAppData = async () => {
    setError(null);
    let userData: User | null = null;
    try {
      userData = await AuthService.getProfile();
      posthog?.identify(userData.id.toString());
    } catch (err) {
      const errorObj = err as AxiosError;
      if (errorObj.response?.status === StatusCodes.FORBIDDEN) {
        setError("forbidden");
        return;
      }

      if (errorObj.response?.status === StatusCodes.UNAUTHORIZED) {
        setError("unauthorized");
        return;
      }
      setError("maintenance");
      return;
    }

    if (userData) {
      resetAllStates();

      if (userData.impersonatedUser !== null) {
        dispatch(setProfile(userData.impersonatedUser));
        dispatch(setParentUser(userData));
      } else {
        dispatch(setProfile(userData));
        dispatch(setParentUser(null));
      }

      const configData = await ConfigurationsService.get([
        { key: "confirmed", value: [true] },
      ]);
      dispatch(setConfig(configData));

      const orgSettingsData = await OrgService.getSettings();
      dispatch(setOrgSettings(orgSettingsData));

      setDataLoaded(true);
    }
  };

  const logout = () => {
    setDataLoaded(false);
    window.location.href = `${process.env.REACT_APP_API_URL}/api/logout`;
  };

  const renderApp = () => {
    if (error) {
      return (
        <ErrorRouter error={error} logout={logout} onRetry={loadAppData} />
      );
    }

    if (!isAuthenticated) {
      return <LoginRouter />;
    }

    return dataLoaded ? (
      <AppRouter
        profile={profile}
        loadAppData={loadAppData}
        setDataLoaded={setDataLoaded}
        logout={logout}
      />
    ) : (
      <Loader loading fullscreen size={45} />
    );
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAppData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (width < theme.breakpoints.LG) {
      setError("notDesktop");
    }
  }, []);

  return renderApp();
};
